import { makeAutoObservable, runInAction } from 'mobx'
import type { Notify } from '@/Ship/Contracts/Notify'
import { CreateExecutorAssignmentTask } from '../Tasks/CreateExecutorAssignmentTask'
import type { VehicleNeedsEditStore } from './VehicleNeedsEditStore'

/**
 * Local store of `AddTaskModal`.
 *
 * Holds the form state for creating a new `ExecutorAssignment` under a given
 * executor. Drives both the dependent-field UI (carrier → driver / vehicle) and
 * the field-level validation badges. On success delegates the local insertion
 * back to the owning `VehicleNeedsEditStore`.
 *
 * `modalKey` is incremented on each open so that the inner FormFieldCells —
 * which are seeded by `initialValue` and never react to prop changes — reset
 * their internal MobX form-context per opening.
 */
export class AddTaskModalStore {
    open = false
    executorUuid: string | null = null
    loading = false
    modalKey = 0
    errors: string[] = []

    isHired = false
    cluster: string | null = null
    clusterObj: any = null
    region: string | null = null
    regionObj: any = null
    carrier: string | null = null
    carrierObj: any = null
    driver: string | null = null
    driverObj: any = null
    vehicle: string | null = null
    vehicleObj: any = null
    plannedLoadingDate: string | null = null
    totalWeightTonnes: number | null = null
    tripsCount: number | null = 1
    eWaybillNumber: string | null = null

    constructor() {
        makeAutoObservable(this)
    }

    openFor(executorUuid: string): void {
        this.modalKey += 1
        this.open = true
        this.executorUuid = executorUuid
        this.loading = false
        this.errors = []
        this.isHired = false
        this.cluster = null
        this.clusterObj = null
        this.region = null
        this.regionObj = null
        this.carrier = null
        this.carrierObj = null
        this.driver = null
        this.driverObj = null
        this.vehicle = null
        this.vehicleObj = null
        this.plannedLoadingDate = null
        this.totalWeightTonnes = null
        this.tripsCount = 1
        this.eWaybillNumber = null
    }

    close(): void {
        this.open = false
        this.executorUuid = null
        this.loading = false
    }

    /** Switching `isHired` resets the carrier — driver / vehicle filters depend on it. */
    setIsHired(v: boolean): void {
        this.isHired = v
        this.carrier = null
        this.carrierObj = null
    }

    /** Switching carrier resets driver / vehicle — both are filtered by carrier. */
    setCarrier(iri: string | null): void {
        this.carrier = iri
        this.driver = null
        this.vehicle = null
        this.dropError('carrier')
    }

    setCarrierObj(obj: any): void {
        this.carrierObj = obj
    }

    /** Switching cluster resets the region — regions are filtered by cluster. */
    setCluster(iri: string | null): void {
        this.cluster = iri
        this.region = null
        this.regionObj = null
        this.dropError('cluster')
    }

    setClusterObj(obj: any): void {
        this.clusterObj = obj
    }

    setRegion(iri: string | null): void {
        this.region = iri
        this.dropError('region')
    }
    setRegionObj(obj: any): void {
        this.regionObj = obj
    }

    setDriver(iri: string | null): void {
        this.driver = iri
        this.dropError('driver')
    }
    setDriverObj(obj: any): void {
        this.driverObj = obj
    }

    setVehicle(iri: string | null): void {
        this.vehicle = iri
        this.dropError('vehicle')
    }
    setVehicleObj(obj: any): void {
        this.vehicleObj = obj
    }

    setTotalWeightTonnes(v: number | null): void {
        this.totalWeightTonnes = v
        if (v != null) this.dropError('totalWeightTonnes')
    }

    setTripsCount(v: number | null): void {
        this.tripsCount = v
        if (v) this.dropError('tripsCount')
    }

    private dropError(field: string): void {
        this.errors = this.errors.filter((e) => e !== field)
    }

    private validate(): string[] {
        const errors: string[] = []
        if (!this.cluster) errors.push('cluster')
        if (!this.region) errors.push('region')
        if (this.isHired && !this.carrier) errors.push('carrier')
        if (!this.driver) errors.push('driver')
        if (!this.vehicle) errors.push('vehicle')
        if (this.totalWeightTonnes == null) errors.push('totalWeightTonnes')
        if (!this.tripsCount) errors.push('tripsCount')
        return errors
    }

    /**
     * Submit the form. On success: append the freshly-created assignment to
     * the owning edit-store (so the table refreshes optimistically) and close
     * the modal.
     */
    async submit(editStore: VehicleNeedsEditStore, notify: Notify): Promise<boolean> {
        if (!this.executorUuid) return false
        const validationErrors = this.validate()
        if (validationErrors.length > 0) {
            this.errors = validationErrors
            return false
        }
        this.errors = []
        this.loading = true
        try {
            const result: any = await CreateExecutorAssignmentTask({
                executor: `/services/vehicle-need-executors/${this.executorUuid}`,
                isHired: this.isHired,
                ...(this.cluster ? { cluster: this.cluster } : {}),
                ...(this.region ? { region: this.region } : {}),
                ...(this.carrier ? { carrier: this.carrier } : {}),
                ...(this.driver ? { driver: this.driver } : {}),
                ...(this.vehicle ? { vehicle: this.vehicle } : {}),
                ...(this.plannedLoadingDate ? { plannedLoadingDate: this.plannedLoadingDate } : {}),
                ...(this.totalWeightTonnes != null ? { totalWeightTonnes: this.totalWeightTonnes } : {}),
                ...(this.tripsCount != null ? { tripsCount: this.tripsCount } : {}),
                ...(this.eWaybillNumber ? { eWaybillNumber: this.eWaybillNumber } : {}),
            })
            const resultEntry = {
                ...result,
                ...(this.clusterObj ? { cluster: this.clusterObj } : {}),
                ...(this.regionObj ? { region: this.regionObj } : {}),
                ...(this.carrierObj ? { carrier: this.carrierObj } : {}),
                ...(this.driverObj ? { driver: this.driverObj } : {}),
                ...(this.vehicleObj ? { vehicle: this.vehicleObj } : {}),
            }
            editStore.appendAssignment(this.executorUuid, resultEntry)
            notify.success('Завдання додано')
            this.close()
            return true
        } catch (e: any) {
            notify.error(e?.message || 'Не вдалося додати завдання')
            runInAction(() => {
                this.loading = false
            })
            return false
        }
    }
}
